import type { IExtendedStyleProperties } from '@themes/types/extended';

import type { PaginationProps } from '@mui/material';
import type { Theme } from '@mui/material/styles';

import { getColors } from '@themes/utils/getColors';

interface IPaginationStyleProperties extends IExtendedStyleProperties {
  variant: PaginationProps['variant'];
}

function getColorStyle({ theme, color, variant }: IPaginationStyleProperties) {
  const colors = getColors(theme, color);
  const { main, dark, light, lighter, contrastText } = colors;

  const focusStyle = {
    '&:focus-visible': {
      outlineOffset: 2,
      outline: `2px solid ${dark}`,
      ...(variant === 'text' && {
        backgroundColor: theme.palette.background.paper,
      }),
    },
  };

  switch (variant) {
    case 'combined':
    case 'contained': {
      return {
        color: contrastText,
        backgroundColor: main,
        '&:hover': {
          backgroundColor: light,
        },
        ...focusStyle,
      };
    }
    case 'outlined': {
      return {
        borderColor: main,
        '&:hover': {
          borderColor: light,
          backgroundColor: lighter,
        },
        ...focusStyle,
      };
    }
    default: {
      return {
        color: main,
        '&:hover': {
          color: lighter,
          backgroundColor: main,
        },
        ...focusStyle,
      };
    }
  }
}

export default function PaginationItem(theme: Theme) {
  return {
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          '&:focus-visible': {
            outlineOffset: 2,
            outline: `2px solid ${theme.palette.secondary.dark}`,
          },
        },
        contained: {
          '&.Mui-selected': {
            '&.MuiPaginationItem-containedInfo': getColorStyle({
              theme,
              color: 'info',
              variant: 'contained',
            }),
            '&.MuiPaginationItem-containedError': getColorStyle({
              theme,
              color: 'error',
              variant: 'contained',
            }),
            '&.MuiPaginationItem-containedWarning': getColorStyle({
              theme,
              color: 'warning',
              variant: 'contained',
            }),
            '&.MuiPaginationItem-containedSuccess': getColorStyle({
              theme,
              color: 'success',
              variant: 'contained',
            }),
            '&.MuiPaginationItem-containedPrimary': getColorStyle({
              theme,
              color: 'primary',
              variant: 'contained',
            }),
            '&.MuiPaginationItem-containedSecondary': getColorStyle({
              theme,
              color: 'secondary',
              variant: 'contained',
            }),
          },
        },
        text: {
          '&.Mui-selected': {
            fontWeight: 500,
            fontSize: '1rem',
            backgroundColor: 'transparent',
            '&.MuiPaginationItem-textInfo': getColorStyle({
              theme,
              color: 'info',
              variant: 'text',
            }),
            '&.MuiPaginationItem-textError': getColorStyle({
              theme,
              color: 'error',
              variant: 'text',
            }),
            '&.MuiPaginationItem-textWarning': getColorStyle({
              theme,
              variant: 'text',
              color: 'warning',
            }),
            '&.MuiPaginationItem-textSuccess': getColorStyle({
              theme,
              variant: 'text',
              color: 'success',
            }),
            '&.MuiPaginationItem-text.MuiPaginationItem-colorPrimary':
              getColorStyle({
                theme,
                variant: 'text',
                color: 'primary',
              }),
            '&.MuiPaginationItem-text.MuiPaginationItem-colorSecondary':
              getColorStyle({
                theme,
                variant: 'text',
                color: 'secondary',
              }),
          },
        },
        combined: {
          border: '1px solid',
          borderColor: theme.palette.divider,
          '&.MuiPaginationItem-ellipsis': {
            border: 'none',
          },
          '&.Mui-selected': {
            '&.MuiPaginationItem-combinedInfo': getColorStyle({
              theme,
              color: 'info',
              variant: 'combined',
            }),
            '&.MuiPaginationItem-combinedError': getColorStyle({
              theme,
              color: 'error',
              variant: 'combined',
            }),
            '&.MuiPaginationItem-combinedWarning': getColorStyle({
              theme,
              color: 'warning',
              variant: 'combined',
            }),
            '&.MuiPaginationItem-combinedSuccess': getColorStyle({
              theme,
              color: 'success',
              variant: 'combined',
            }),
            '&.MuiPaginationItem-combinedPrimary': getColorStyle({
              theme,
              color: 'primary',
              variant: 'combined',
            }),
            '&.MuiPaginationItem-combinedSecondary': getColorStyle({
              theme,
              color: 'secondary',
              variant: 'combined',
            }),
          },
        },
        outlined: {
          borderColor: theme.palette.divider,
          '&.Mui-selected': {
            backgroundColor: 'transparent',
            '&.MuiPaginationItem-outlinedInfo': getColorStyle({
              theme,
              color: 'info',
              variant: 'outlined',
            }),
            '&.MuiPaginationItem-outlinedError': getColorStyle({
              theme,
              color: 'error',
              variant: 'outlined',
            }),
            '&.MuiPaginationItem-outlinedWarning': getColorStyle({
              theme,
              color: 'warning',
              variant: 'outlined',
            }),
            '&.MuiPaginationItem-outlinedSuccess': getColorStyle({
              theme,
              color: 'success',
              variant: 'outlined',
            }),
            '&.MuiPaginationItem-outlined.MuiPaginationItem-colorPrimary':
              getColorStyle({
                theme,
                color: 'primary',
                variant: 'outlined',
              }),
            '&.MuiPaginationItem-outlined.MuiPaginationItem-colorSecondary':
              getColorStyle({
                theme,
                color: 'secondary',
                variant: 'outlined',
              }),
          },
        },
      },
    },
  };
}
